import React, { useEffect, useState } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import { api } from "../../lib/api";
import { Loader2, CheckCircle2, AlertTriangle } from "lucide-react";
import { useSettings } from "../../lib/useSettings";

export default function PodSsoConsumePage() {
  const [params] = useSearchParams();
  const nav = useNavigate();
  const { settings: s } = useSettings();
  const [state, setState] = useState({ status: "loading", message: "Authentification SSO en cours…" });

  useEffect(() => {
    const token = params.get("token");
    const viewMode = params.get("view_mode") || "admin";
    if (!token) {
      setState({ status: "error", message: "Token SSO manquant dans l'URL" });
      return;
    }
    let cancelled = false;
    (async () => {
      try {
        const { data } = await api.post("/agency/sso/consume", { token });
        if (cancelled) return;
        // Persist session — same keys as /lib/auth.jsx
        localStorage.setItem("kap_token", data.token);
        // Inject view_mode from URL OR backend response (URL wins for safety)
        const finalUser = {
          ...(data.user || {}),
          view_mode: viewMode === "client" ? "client" : (data.view_mode || data.user?.view_mode || "admin"),
        };
        localStorage.setItem("kap_user", JSON.stringify(finalUser));
        if (data.update_token) {
          localStorage.setItem("kap_update_token", data.update_token);
        }
        const greet = finalUser.view_mode === "client"
          ? "Mode lecture (agence) — redirection…"
          : `Bienvenue, ${finalUser.first_name || data.email} — redirection…`;
        setState({ status: "ok", message: greet });
        setTimeout(() => {
          window.location.replace("/admin/dashboard");
        }, 800);
      } catch (e) {
        if (cancelled) return;
        const detail = e?.response?.data?.detail || e.message || "Erreur inconnue";
        setState({ status: "error", message: `Échec SSO : ${detail}` });
      }
    })();
    return () => { cancelled = true; };
  }, [params]);

  const company = s?.company_name || "Kloub'Air Plomberie";

  return (
    <div className="admin-scope min-h-screen flex items-center justify-center bg-[color:var(--brand-navy)] p-6" data-testid="admin-sso-consume-page">
      <div className="max-w-md w-full bg-white rounded-2xl shadow-2xl p-8 text-center">
        {s?.logo_url && (
          <img src={s.logo_url} alt={company} className="h-14 w-auto mx-auto mb-5 drop-shadow" />
        )}
        <div className="text-[10px] uppercase tracking-[0.25em] text-[color:var(--brand-sky)] font-bold mb-2">
          Connexion SSO Agence
        </div>

        {state.status === "loading" && (
          <>
            <Loader2 className="w-10 h-10 mx-auto animate-spin text-[color:var(--brand-sky)] mb-3" data-testid="sso-loader" />
            <h1 className="font-display text-2xl font-bold text-[color:var(--brand-navy)] mb-2">
              Authentification en cours
            </h1>
            <p className="text-sm text-[hsl(var(--muted-foreground))]">{state.message}</p>
          </>
        )}

        {state.status === "ok" && (
          <>
            <div className="w-14 h-14 mx-auto rounded-full bg-green-100 flex items-center justify-center mb-3" data-testid="sso-success">
              <CheckCircle2 className="w-8 h-8 text-green-600" />
            </div>
            <h1 className="font-display text-2xl font-bold text-[color:var(--brand-navy)] mb-2">
              Connexion établie
            </h1>
            <p className="text-sm text-[hsl(var(--muted-foreground))]">{state.message}</p>
          </>
        )}

        {state.status === "error" && (
          <>
            <div className="w-14 h-14 mx-auto rounded-full bg-red-100 flex items-center justify-center mb-3" data-testid="sso-error">
              <AlertTriangle className="w-8 h-8 text-red-600" />
            </div>
            <h1 className="font-display text-2xl font-bold text-[color:var(--brand-navy)] mb-2">
              Échec de la connexion
            </h1>
            <p className="text-sm text-red-700 mb-5 break-words">{state.message}</p>
            <a
              href="/admin/login"
              className="inline-flex items-center px-4 py-2 rounded-lg bg-[color:var(--brand-navy)] text-white text-sm font-semibold hover:opacity-90"
              data-testid="sso-fallback-login-btn"
            >
              Se connecter manuellement
            </a>
          </>
        )}
      </div>
    </div>
  );
}
